import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ApiError, api } from '../api/client.js'
import type { Template } from '../api/types.js'
import { EmptyState, Field, Modal, Notice, Spinner } from '../components/ui.js'
import { Icon } from '../components/icons.js'
import { useToast } from '../components/toast.js'
import { PHOTO_REQUIREMENT_LABEL } from '../lib/format.js'
import { PageHead } from './Layout.js'

export function TemplatesPage() {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<Template | 'new' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [options, setOptions] = useState<string[]>([])
  const [reasons, setReasons] = useState<boolean[]>([])
  // Per Textbaustein: does the photo requirement apply? Off for "kein Zugang".
  const [photoOn, setPhotoOn] = useState<boolean[]>([])
  const [photoReq, setPhotoReq] = useState<Template['photoRequirement']>('after')
  const [callback, setCallback] = useState<boolean[]>([])
  const toast = useToast()

  function startEditing(target: Template | 'new') {
    setOptions(target === 'new' ? [] : target.textOptions)
    setReasons(target === 'new' ? [] : target.textOptions.map((o) => (target.reasonOptions ?? []).includes(o)))
    setPhotoOn(target === 'new' ? [] : target.textOptions.map((o) => !(target.photoFreeOptions ?? []).includes(o)))
    setPhotoReq(target === 'new' ? 'after' : target.photoRequirement)
    setCallback(target === 'new' ? [] : target.textOptions.map((o) => (target.callbackOptions ?? []).includes(o)))
    setEditing(target)
  }

  const { data, isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => api<Template[]>('/api/templates'),
  })
  const [params, setParams] = useSearchParams()
  const editParam = params.get('bearbeiten')
  // Linked from a plan or a task: open that Leistung straight away.
  useEffect(() => {
    if (!editParam || !data) return
    const target = data.find((t) => t.id === editParam)
    if (target) startEditing(target)
    const next = new URLSearchParams(params)
    next.delete('bearbeiten')
    setParams(next, { replace: true })
  }, [editParam, data]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id ? api(`/api/templates/${id}`, { method: 'PATCH', body }) : api('/api/templates', { method: 'POST', body }),
    onSuccess: async () => {
      setEditing(null)
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['templates'] })
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  const restore = useMutation({
    mutationFn: (id: string) => api(`/api/templates/${id}/restore`, { method: 'POST' }),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['templates'] }),
  })

  const archive = useMutation({
    mutationFn: (template: Template) => api(`/api/templates/${template.id}`, { method: 'DELETE' }),
    onSuccess: async (_result, template) => {
      toast(
        template.planCount > 0
          ? `„${template.name}“ archiviert, ${template.planCount} Pläne wurden deaktiviert.`
          : `„${template.name}“ archiviert.`,
        { action: { label: 'Rückgängig', onAction: () => restore.mutate(template.id) } },
      )
      await queryClient.invalidateQueries({ queryKey: ['templates'] })
    },
  })

  const current = editing === 'new' ? null : editing

  return (
    <>
      <PageHead
        title="Leistungen"
        subtitle="Was gemacht wird, mit Fotopflicht und, wo nötig, festen Textbausteinen, nach denen abgerechnet wird."
        action={
          <button type="button" className="btn" onClick={() => startEditing('new')}>
            <Icon name="plus" /> Neue Leistung
          </button>
        }
      />

      {error && <Notice kind="error">{error}</Notice>}

      <section className="card">
        {isLoading ? (
          <Spinner />
        ) : !data || data.length === 0 ? (
          <EmptyState
            title="Noch keine Leistungen"
            hint="Zum Beispiel „Treppenhausreinigung“ oder „Legionellenprüfung“."
            action={
              <button type="button" className="btn" onClick={() => startEditing('new')}>
                Neue Leistung
              </button>
            }
          />
        ) : (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Leistung</th>
                  <th>Textbausteine</th>
                  <th>Fotopflicht</th>
                  <th className="nowrap">Dauer</th>
                  <th className="nowrap">Pläne</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.map((template) => (
                  <tr key={template.id}>
                    <td>
                      <span className="strong">{template.name}</span>
                      {template.description && <div className="muted small">{template.description}</div>}
                      {(template.measurementLabel || template.signature !== 'none') && (
                        <div className="chips" style={{ marginTop: 4 }}>
                          {template.measurementLabel && (
                            <span className="chip">
                              Messwert: {template.measurementLabel}
                              {template.measurementUnit ? ` in ${template.measurementUnit}` : ''}
                            </span>
                          )}
                          {template.signature !== 'none' && (
                            <span className="chip">Unterschrift {template.signature === 'required' ? 'Pflicht' : 'freiwillig'}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      {template.textOptions.length === 0 ? (
                        <span className="muted small">frei</span>
                      ) : (
                        <div className="chips">
                          {template.textOptions.map((option) => (
                            <span key={option} className="chip">
                              {option}
                              {(template.reasonOptions ?? []).includes(option) ? ', mit Begründung' : ''}
                              {template.photoRequirement !== 'none' && (template.photoFreeOptions ?? []).includes(option) ? ', ohne Foto' : ''}
                              {(template.callbackOptions ?? []).includes(option) ? ', Rückruf' : ''}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>{PHOTO_REQUIREMENT_LABEL[template.photoRequirement]}</td>
                    <td className="nowrap">
                      {template.estimatedMinutes ? `${template.estimatedMinutes} Min.` : <span className="muted">–</span>}
                    </td>
                    <td className="nowrap">
                      {template.planCount > 0 ? <Link to={`/plaene?leistung=${template.id}`}>{template.planCount}</Link> : <span className="muted">0</span>}
                    </td>
                    <td style={{ textAlign: 'right' }} className="nowrap">
                      <button type="button" className="btn ghost small" onClick={() => startEditing(template)}>
                        Bearbeiten
                      </button>
                      <button
                        type="button"
                        className="btn ghost small"
                        onClick={() => archive.mutate(template)}
                      >
                        Archivieren
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal
        open={editing !== null}
        title={current ? 'Leistung bearbeiten' : 'Neue Leistung'}
        onClose={() => setEditing(null)}
        footer={
          <>
            <button type="button" className="btn secondary" onClick={() => setEditing(null)}>
              Abbrechen
            </button>
            <button type="submit" form="template-form" className="btn" disabled={save.isPending}>
              Speichern
            </button>
          </>
        }
      >
        <form
          id="template-form"
          key={current?.id ?? 'new'}
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            const form = new FormData(event.currentTarget)
            const minutes = form.get('estimatedMinutes')
            save.mutate({
              id: current?.id,
              body: {
                name: form.get('name'),
                description: form.get('description') || undefined,
                photoRequirement: form.get('photoRequirement'),
                estimatedMinutes: minutes ? Number(minutes) : undefined,
                textOptions: options.map((o) => o.trim()).filter(Boolean),
                reasonOptions: options.filter((o, i) => reasons[i] && o.trim()).map((o) => o.trim()),
                photoFreeOptions: options.filter((o, i) => photoOn[i] === false && o.trim()).map((o) => o.trim()),
                callbackOptions: options.filter((o, i) => callback[i] && o.trim()).map((o) => o.trim()),
                legionella: form.get('legionella') === 'on',
                measurementLabel: String(form.get('measurementLabel') ?? '').trim() || null,
                measurementUnit: String(form.get('measurementUnit') ?? '').trim() || null,
                signature: form.get('signature') ?? 'none',
              },
            })
          }}
        >
          <Field label="Bezeichnung">
            <input name="name" required autoFocus defaultValue={current?.name} />
          </Field>
          <Field label="Beschreibung" hint="Wird dem Mitarbeiter in der App angezeigt.">
            <textarea name="description" defaultValue={current?.description ?? ''} />
          </Field>
          <label className="check" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
            <input type="checkbox" name="legionella" defaultChecked={current?.legionella ?? false} />
            Legionellen-Leistung: erscheint im Bereich Legionellen mit Monatsliste und Monatsreport, in der App mit Mangelmeldung.
          </label>
          <div className="row">
            <Field label="Fotopflicht">
              <select name="photoRequirement" value={photoReq} onChange={(e) => setPhotoReq(e.target.value as Template['photoRequirement'])}>
                <option value="none">Kein Foto</option>
                <option value="after">Foto nachher</option>
                <option value="before_after">Foto vorher und nachher</option>
              </select>
            </Field>
            <Field label="Richtzeit in Minuten">
              <input name="estimatedMinutes" type="number" min={0} max={600} defaultValue={current?.estimatedMinutes ?? ''} />
            </Field>
          </div>
          <div className="row">
            <Field label="Messwert (freiwillig)" hint="Wird beim Abhaken abgefragt, z. B. Wassertemperatur bei der Legionellenprüfung.">
              <input name="measurementLabel" defaultValue={current?.measurementLabel ?? ''} placeholder="z. B. Wassertemperatur" maxLength={80} />
            </Field>
            <Field label="Einheit">
              <input name="measurementUnit" defaultValue={current?.measurementUnit ?? ''} placeholder="z. B. °C oder m³" maxLength={20} />
            </Field>
            <Field label="Unterschrift vor Ort" hint="Z. B. vom Mieter bei Arbeiten in der Wohnung.">
              <select name="signature" defaultValue={current?.signature ?? 'none'}>
                <option value="none">keine</option>
                <option value="optional">freiwillig</option>
                <option value="required">Pflicht</option>
              </select>
            </Field>
          </div>

          <div className="field">
            <span>Textbausteine</span>
            <div className="hint" style={{ marginBottom: 8 }}>
              Feste Auswahl, aus der der Mitarbeiter beim Abhaken eine Option wählen muss, z. B. „Begehung und Spülung“ oder „nur
              Begehung“. Danach richtet sich die Abrechnung. Leer lassen, wenn es nichts zu unterscheiden gibt. Mit „Begründung Pflicht“
              muss der Mitarbeiter bei dieser Auswahl kurz schreiben, was los war. Ohne „Foto Pflicht“ entfällt das Foto bei dieser Auswahl,
              z. B. bei „kein Zugang“. Mit „Rückruf“ wird die Aufgabe in der Disposition markiert, weil jemand nachfassen muss.
            </div>
            <div className="text-options">
              {options.map((option, index) => (
                <div className="opt" key={index}>
                  <span className="muted small" style={{ width: 18 }}>
                    {index + 1}
                  </span>
                  <input
                    value={option}
                    placeholder="Textbaustein"
                    onChange={(e) => setOptions(options.map((o, i) => (i === index ? e.target.value : o)))}
                  />
                  <label className="reason-toggle" title="Der Mitarbeiter muss bei dieser Auswahl kurz begründen, z. B. bei „kein Zugang“.">
                    <input
                      type="checkbox"
                      checked={reasons[index] ?? false}
                      onChange={(e) => setReasons(options.map((_, i) => (i === index ? e.target.checked : (reasons[i] ?? false))))}
                    />
                    Begründung Pflicht
                  </label>
                  {photoReq !== 'none' && (
                    <label className="reason-toggle" title="Bei dieser Auswahl gilt die Fotopflicht. Aus z. B. bei „kein Zugang“.">
                      <input
                        type="checkbox"
                        checked={photoOn[index] ?? true}
                        onChange={(e) => setPhotoOn(options.map((_, i) => (i === index ? e.target.checked : (photoOn[i] ?? true))))}
                      />
                      Foto Pflicht
                    </label>
                  )}
                  <label className="reason-toggle" title="Nach dieser Auswahl muss die Disposition nachfassen, die Aufgabe wird markiert.">
                    <input
                      type="checkbox"
                      checked={callback[index] ?? false}
                      onChange={(e) => setCallback(options.map((_, i) => (i === index ? e.target.checked : (callback[i] ?? false))))}
                    />
                    Rückruf
                  </label>
                  <button
                    type="button"
                    className="btn ghost small"
                    aria-label="Entfernen"
                    onClick={() => {
                      setOptions(options.filter((_, i) => i !== index))
                      setReasons(reasons.filter((_, i) => i !== index))
                      setPhotoOn(photoOn.filter((_, i) => i !== index))
                      setCallback(callback.filter((_, i) => i !== index))
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <div>
                <button
                  type="button"
                  className="btn secondary small"
                  onClick={() => {
                    setOptions([...options, ''])
                    setReasons([...reasons, false])
                    setPhotoOn([...photoOn, true])
                    setCallback([...callback, false])
                  }}
                >
                  Textbaustein hinzufügen
                </button>
              </div>
            </div>
          </div>
        </form>
      </Modal>
    </>
  )
}
